<template>
  <Container :plan="form.plan">
    <template v-slot:content>
      <div class="font-refresh registration-main" v-if="!submitted">
        <h1 class="font-weight-bold">Sign up for a 14-day free trial</h1>
        <p class="push-20-t text-body">After sign-up, you'll be able to:</p>
        <ul>
          <li>Get instant access to the AdvicePay platform while we verify your account</li>
          <li>Create one-time or recurring invoices for your clients</li>
          <li>Accept card and ACH payments</li>
        </ul>

        <form @submit.prevent="submit" class="push-20-t">
          <FormField class="font-weight-bold" label="Select your plan:" :val="form.plan" :v="formValidations.plan">
            <div class="row">
              <div class="col-xs-4 push-5-t pad-5">
                <RadioTile v-model="form.plan" :inputVal="accountTypes.ESSENTIAL" class="plan-tile text-center" :primaryColor="primaryColor(accountTypes.ESSENTIAL)" :secondaryColor="secondaryColor(accountTypes.ESSENTIAL)">
                  <img src="/assets/img/money.svg" class="img-fluid pull-10-t" />
                  <div class="push-15-t dis-block fw-600">Essential</div>
                </RadioTile>
              </div>

              <div class="col-xs-4 push-5-t pad-5">
                <RadioTile v-model="form.plan" :inputVal="accountTypes.PROFESSIONAL" class="plan-tile text-center" :primaryColor="primaryColor(accountTypes.PROFESSIONAL)" :secondaryColor="secondaryColor(accountTypes.PROFESSIONAL)">
                  <img src="/assets/img/people.svg" class="pull-10-t" />
                  <div class="push-15-t dis-block fw-600">Professional</div>
                </RadioTile>
              </div>

              <div class="col-xs-4 push-5-t pad-5">
                <RadioTile v-model="form.plan" :inputVal="accountTypes.ENTERPRISE" class="plan-tile text-center" :primaryColor="primaryColor(accountTypes.ENTERPRISE)" :secondaryColor="secondaryColor(accountTypes.ENTERPRISE)">
                  <img src="/assets/img/bank_building.svg" class="pull-10-t" />
                  <div class="push-15-t dis-block fw-600">Enterprise</div>
                </RadioTile>
              </div>
            </div>
          </FormField>

          <FormField label="Email Address:" :val="form.email" :v="formValidations.email" class="push-20-t" v-slot="slotProps">
            <input class="form-control rounded" v-model.trim="form.email" :id="slotProps.id">
          </FormField>
          

          <div class="row push-10-t">
              <div class="text-left col-xs-7 push-10-t align-middle">
                  <p class="push-5-t">Already have an account? <a href="/">Sign in</a></p>
              </div>
              <div class="text-right col-xs-5 push-10-t">
                <a v-if="form.plan == accountTypes.ENTERPRISE" class="btn push-10-l rounded-5 registration-submit-btn" href="mailto:sales@advicepay.com" target="_blank">Talk to Sales</a>
                <button v-else class="btn push-10-l registration-submit-btn" type="submit">Get Started</button>
              </div>
          </div>
        </form>
      </div>

      <div v-if="submitted" class="text-center font-refresh">
        <img src="/assets/img/success.svg" />

        <div v-if="alreadyVerified">
          <h1 class="h1 push-20-t">User added!</h1>
          <p class="push-15-t">A new Advisor account has been added to your existing login.</p>
          <hr>
          <a class="btn btn-primary" :href="baseURL">Close</a>
        </div>

        <div v-else>
          <h1 class="h1 push-20-t">Check your email!</h1>
          <p class="push-15-t">A link to activate your account has been emailed to the address provided.</p>
          <hr>
          <a class="btn btn-primary" :href="baseURL">Close</a>
          <div class="push-15-t">
            <a href="#" @click.prevent="resend">Re-send verification link</a>
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script lang="ts">
import { Component, Prop, Watch , Vue } from 'vue-facing-decorator'; 
import Container from '../shared/RefreshContainer.vue';
import RadioTile from '../shared/RefreshRadioTile.vue';
import FormField from '../../shared/FormField.vue';
import UserConstants from '../../../constants/users';
import RegistrationAPI from '../../../api/modules/registration';
import HTTPConstants from '../../../constants/http';
import PlanConstants from '../../../constants/refreshPlans';


@Component({
  components: {
    Container,
    RadioTile,
    FormField
  }
})
export default class Main extends Vue {
  @Prop({ type: String, required: true }) plan!: string;
  @Prop({ type: String, required: true }) coupon!: string;
  @Prop({ type: String, required: true }) googleAnalyticsId!: string;
  @Prop({ type: String }) email!: string;
  @Prop({ type: String }) token!: string | undefined;
  @Prop({ type: String, required: true }) baseURL!: string;

  accountTypes = UserConstants.accountTypes;

  submitted = false;
  duplicate = false;
  alreadyVerified = false;
  hasSetPlan = false;
  form: {coupon?: string, verifyToken?: string, plan: string, email: string} = {
    plan: '',
    coupon: '',
    email: '',
    verifyToken: ''
  }

  created() {
    this.form.verifyToken = this.token;
    this.form.plan = this.plan;
    this.form.coupon = this.coupon;
    this.form.email = this.email;
    this.sendGALoadedEvent();

    if (!!this.token && !!this.email) {
      this.submit();
    }
  }

  @Watch('form.plan')
  onPlanChange() {
    // Since the page will be coming in with professional=true set and will have sent a page view request, don't send the first virtual page view.
    if (this.hasSetPlan) {
      this.sendGAPageView('/signup?plan=' + this.form.plan);
    }
    else {
      this.hasSetPlan = true;
    }
  }

  submit() {
    if (!this.isValidForm(this.form, this.formValidations)) {
      return
    }

    RegistrationAPI.new(this.form).then(
      (success: any) => {
        this.submitted = true;
        this.alreadyVerified = success.data.verified;
        window.App.loader('hide');
        this.sendGASubmittedEvent();
      },
      (failure) => {
        if (failure.data.code === HTTPConstants.statuses.CONFLICT) {
          this.$swal('Oops...', 'The maximum number of accounts with that email has been reached', 'warning');
        } else if (failure.data.code === HTTPConstants.statuses.UNPROCESSABLE_ENTITY) {
          this.$swal('Oops...', 'Invalid Email', 'warning');
        } else if  (failure.data.code === HTTPConstants.statuses.TEAPOT) {
          this.submitted = true;
          this.duplicate = true;
        } else if  (failure.data.code === HTTPConstants.statuses.NOT_FOUND) {
          this.$swal('Oops...', 'Invalid Link', 'error').then(() => {
            window.location.href = '/';
          });
        } else {
          this.genericError();
        }
        window.App.loader('hide');
      }
    )
  }
  resend() {
    const data: { coupon: string, email: string, plan?: string | undefined } = { coupon: this.form.coupon!, email: this.form.email, plan: undefined };
    if (this.duplicate) {
      data.plan = this.form.plan;
    }
    RegistrationAPI.email(data).then(
      () => {
        window.$.notify({ message: 'Verification email has been re-sent' }, { type: 'success' });
        window.App.loader('hide');
      },
      () => { this.genericError(); }
    )
  }
  sendGALoadedEvent() {
    window.gtag('event', 'signup_loaded', { 'event_category': 'onboarding', 'event_label': 'plan', 'value': this.form.plan });
  }
  sendGASubmittedEvent() {
    window.gtag('event', 'signup_submitted', { 'event_category': 'onboarding', 'event_label': 'plan', 'value': this.form.plan });
  }
  sendGAPageView(url: string){
    window.gtag('config', this.googleAnalyticsId, {
      'send_page_view': true,
      'page_path': url,
    });
    window.gtag('config', this.googleAnalyticsId, { send_page_view: false });
  }

  secondaryColor(plan: string): string {
      if (plan !== this.form.plan) {return ''};
      return this.planData.secondaryColor;
  }
  primaryColor(plan: string): string {
      if (plan !== this.form.plan) {return ''};
      return this.planData.primaryColor;
  }

  get planData(): any {
      return PlanConstants.plans.TYPES.find((el: { key: string }) => { return el.key == this.form.plan });
  }

  get formValidations() {
    return {
      plan: { label: 'Plan', required: true, in: [this.accountTypes.ESSENTIAL, this.accountTypes.PROFESSIONAL, this.accountTypes.ENTERPRISE] },
      email: { required: true, email: true },
    }
  }
}
</script>
